import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  output,
  Output,
  OutputEmitterRef,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  InputDirective,
  InputPatternDirective,
  LabelDirective,
  OptionDirective,
  SelectComponent,
  ValidationMessageComponent,
  ValidationMessagesComponent,
} from '@shared/ui-forms';
import { distinctUntilChanged, filter, map, NEVER, Observable, tap } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BUSINESS_DOMAIN_MAPPING,
  BUSINESS_DOMAINS,
  BusinessDomainType,
  OTHER_DOMAIN,
} from '../../constants/business-domains.constant';
import { CompanyDetails } from '../../models/company-details.model';

const CSL_IMPORTS = [
  InputDirective,
  InputPatternDirective,
  SelectComponent,
  OptionDirective,
  ValidationMessagesComponent,
  ValidationMessageComponent,
  LabelDirective,
];

@Component({
  selector: 'csl-company-details-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ...CSL_IMPORTS],
  templateUrl: './company-details-form.component.html',
  styleUrl: './company-details-form.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyDetailsFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  BUSINESS_DOMAINS = BUSINESS_DOMAINS;
  OTHER_DOMAIN = OTHER_DOMAIN;
  companyNamePattern = /^[a-zA-Z0-9 ,;:'\-().]*$/;
  customDomainPattern = /^[a-zA-Z0-9 !"#$%&'()*+,\-./:;<=>?@[\\\]^_{|}~`]*$/;
  form: CompanyDetailsForm = this.initForm();
  readonly errorIds: Record<CompanyDetailsControlName, string> = {
    businessDomain: 'businessDomain-error',
    customDomain: 'customDomain-error',
    companyName: 'companyName-error',
  };
  errorMessages: { [key in CompanyDetailsControlName]: { [key: string]: string } } = {
    businessDomain: {
      required: 'Business type is required.',
    },
    customDomain: {
      required: 'Custom business type is required.',
      pattern: 'Business type can include letters, numbers, spaces, and punctuation.',
      minlength: 'Business type must be at least 2 characters.',
    },
    companyName: {
      required: 'Company name is required.',
      minlength: 'Company name must be at least 2 characters.',
    },
  };
  private _isDisabled = false;

  @Input() set domain(v: BusinessDomainType | string | null) {
    if (v) {
      const mappedValue = BUSINESS_DOMAIN_MAPPING[v] || v;
      if (BUSINESS_DOMAINS.includes(mappedValue as BusinessDomainType)) {
        this.form.controls.businessDomain?.setValue(mappedValue, { emitEvent: false });
      } else {
        this.form.controls.businessDomain?.setValue(OTHER_DOMAIN, { emitEvent: false });
        this.toggleCustomDomain(OTHER_DOMAIN);
        this.form.controls.customDomain?.setValue(v, { emitEvent: false });
      }
    } else {
      this.form.reset();
    }
  }

  @Input() set isDisabled(v: boolean) {
    if (v) {
      this.form.disable();
    } else {
      this.form.enable();
    }
    this._isDisabled = v;
  }

  @Input() set isDomainDisabled(v: boolean) {
    if (v || this._isDisabled) {
      this.form.controls.businessDomain.disable();
    } else {
      this.form.controls.businessDomain.enable();
    }
  }

  @Input() set companyName(v: string | null) {
    if (v) {
      this.form.patchValue({ companyName: v });
    } else {
      this.form.reset();
    }
  }

  @Output() changes = this.form.valueChanges.pipe(
    map((v) => {
      if (this.form.invalid) return null;

      return (this.form.controls.businessDomain.disabled ? this.form.getRawValue() : v) as CompanyDetails;
    }),
  );

  businessDomainChanged = output<string | null | undefined>();
  companyNameChanged = output<string | null | undefined>();
  customDomainChanged = output<string | null | undefined>();
  businessDomainError = output<string>();
  companyNameError = output<string>();
  customDomainError = output<string>();

  constructor() {
    this.form.controls.businessDomain.valueChanges.subscribe((v) => {
      this.toggleCustomDomain(v);
    });
  }

  ngOnInit(): void {
    this.emitControlValue('businessDomain').subscribe((domain) => this.businessDomainChanged.emit(domain));
    this.emitControlValue('companyName').subscribe((name) => this.companyNameChanged.emit(name));
    this.emitControlError('businessDomain', this.businessDomainError);
    this.emitControlError('companyName', this.companyNameError);
  }

  isValidationShouldBeShown(control: AbstractControl): boolean {
    return control.invalid && control.touched;
  }

  private initForm(): CompanyDetailsForm {
    return new FormGroup({
      businessDomain: new FormControl<string | null>(null, [Validators.required]),
      companyName: new FormControl<string | null>(null, [Validators.required, Validators.minLength(2)]),
    });
  }

  private toggleCustomDomain(v: string | null) {
    if (v === OTHER_DOMAIN) {
      this.form.addControl(
        'customDomain',
        new FormControl<string | null>({ value: null, disabled: this._isDisabled }, [
          Validators.required,
          Validators.minLength(2),
          Validators.pattern(this.customDomainPattern),
        ]),
      );

      this.emitControlValue('customDomain').subscribe((customDomain) => this.customDomainChanged.emit(customDomain));
      this.emitControlError('customDomain', this.customDomainError);
    } else {
      this.form.removeControl('customDomain');
    }
  }

  private emitControlValue(controlName: CompanyDetailsControlName): Observable<string | null | undefined> {
    const ctrl = this.form.get(controlName);

    if (!ctrl) return NEVER;

    return ctrl!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef));
  }

  private emitControlError(controlName: CompanyDetailsControlName, output: OutputEmitterRef<string>): void {
    const ctrl = this.form?.controls[controlName] as FormControl;

    ctrl.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(({ source }) => source),
        map((ctrl) => {
          const errorKey = Object.keys(ctrl.errors ?? {})[0]; // get first error

          if (this.isValidationShouldBeShown(ctrl)) {
            const error = this.errorMessages[controlName][errorKey];
            if (error) {
              return error;
            }
          }
          return '';
        }),
        distinctUntilChanged(),
        filter((error) => !!error),
        tap((error) => output.emit(error)),
      )
      .subscribe();
  }
}

type CompanyDetailsFormControls = {
  businessDomain: FormControl<string | null>;
  customDomain?: FormControl<string | null>;
  companyName: FormControl<string | null>;
};

export type CompanyDetailsForm = FormGroup<CompanyDetailsFormControls>;
type CompanyDetailsControlName = keyof CompanyDetailsFormControls;
